/**
 * The selected object track's overlay pattern vocabulary
 * (``map.track.selected_dash_pattern``; REQ-UI-200 amended 2026-10-19) — the
 * UI-side twin of the BFF's ``SelectedDashPatternLiteral``, in the same order,
 * finest first.
 *
 * Its own module, with no imports, because three unrelated places read it:
 * the map layer (:file:`object-track-layer-impl.ts`, which turns a pattern
 * into a ``line-dasharray``), the Profile hook (:file:`use-selected-track-dash.ts`)
 * and the Settings drawer (:file:`profiles/profile-editor.tsx`), and the editor
 * must not pull the layer's MapLibre / store chain in for three string
 * constants. One closed vocabulary on both sides of the API: a pattern the
 * schema accepted that the layer could not draw would be a setting that
 * changes nothing.
 */

/** The patterns a Profile can select, finest first. */
export const SELECTED_DASH_PATTERNS = ["dotted", "short_dash", "long_dash"] as const;

/** One of {@link SELECTED_DASH_PATTERNS}. */
export type SelectedDashPattern = (typeof SELECTED_DASH_PATTERNS)[number];

/** The schema-default pattern (``TrackMapSettings.selected_dash_pattern``). */
export const DEFAULT_SELECTED_DASH_PATTERN: SelectedDashPattern = "dotted";

/**
 * Narrow a stored value to a pattern the layer can draw, else the default.
 *
 * Fail-visible (CLAUDE.md §8.1): an unknown value — a Profile written by a
 * newer UI, or a hand-edited document — falls back to the default pattern
 * rather than to no overlay, because the setting exists precisely because
 * selection was hard to see.
 */
export function normalizeSelectedDashPattern(value: unknown): SelectedDashPattern {
  return (SELECTED_DASH_PATTERNS as readonly unknown[]).includes(value)
    ? (value as SelectedDashPattern)
    : DEFAULT_SELECTED_DASH_PATTERN;
}
